/**
 * Shared constants for training tracks
 * These constants match the backend constants
 */

export enum TrackType {
  KB = 'kb',
  PLAYBOOK = 'playbook'
}

export const TRACK_TYPE_LABELS: Record<TrackType, string> = {
  [TrackType.KB]: 'Knowledge Base',
  [TrackType.PLAYBOOK]: 'Sales Playbook'
};

// The two track types an admin can create, shared by the tracks list's
// create menu and the builder's Track Type select.
export const TRACK_TYPE_OPTION_LABELS: Record<TrackType, string> = {
  [TrackType.KB]: 'Knowledge Base Items',
  [TrackType.PLAYBOOK]: 'Sales Playbook'
};

export const TRACK_TYPE_HELPER_TEXT: Record<TrackType, string> = {
  [TrackType.KB]:
    'The AI customer reads out each knowledge base item in the order you set, one at a time. ' +
    'The rep answers each one, and every answer is scored after the session.',
  [TrackType.PLAYBOOK]:
    'A live, free-flowing call: the rep runs the selected sales playbook while an AI customer you ' +
    'describe raises the knowledge base items chosen for them.'
};

// Sales Playbook (Playbook Live) tracks only; knowledge base tracks have no
// difficulty. Single knob: drives both scoring strictness (acceptance
// threshold) and pushback persistence (max pushback attempts per item).
// Values must match DIFFICULTY_CONFIG in services/websocket/playbook_live/config.py.
export enum DifficultyLevel {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard'
}

export const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  [DifficultyLevel.EASY]: 'Easy: fairly open, 60% accuracy to pass, 2 pushback attempts',
  [DifficultyLevel.MEDIUM]: 'Medium: claims need backing, 75% accuracy to pass, 3 pushback attempts',
  [DifficultyLevel.HARD]: 'Hard: assumes it is oversold, 90% accuracy to pass, 5 pushback attempts'
};

export const DIFFICULTY_CONFIG: Record<DifficultyLevel, { acceptanceThreshold: number; maxPushbackRounds: number }> = {
  [DifficultyLevel.EASY]: { acceptanceThreshold: 60, maxPushbackRounds: 2 },
  [DifficultyLevel.MEDIUM]: { acceptanceThreshold: 75, maxPushbackRounds: 3 },
  [DifficultyLevel.HARD]: { acceptanceThreshold: 90, maxPushbackRounds: 5 }
};

/** How the AI customer sounds (personaConfig.personality). */
export const MANNER_LABELS: Record<string, string> = {
  neutral: 'Neutral',
  curious: 'Curious',
  frustrated: 'Frustrated',
  aggressive: 'Aggressive',
};

/** How much the AI customer talks (personaConfig.talkativeness). */
export const TALKATIVENESS_LABELS: Record<string, string> = {
  terse: 'Terse, short answers',
  normal: 'Normal',
  chatty: 'Chatty, wanders',
};

/** Error code the backend returns when a track's AI customer opening line is not a greeting. */
export const OPENING_LINE_INVALID = 'OPENING_LINE_INVALID';
